import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import * as d3 from "d3-geo";
import { feature, mesh } from "topojson-client";
import { SiteData } from '../types';
import { useMapData, BubbleData } from './map/useMapData';
import { useMapInteractions } from './map/useMapInteractions';
import { MapBubbles } from './map/MapBubbles';
import { MapTooltip } from './map/MapTooltip';
import { MapControls } from './map/MapControls';
import { SiteDetailsModal } from './map/SiteDetailsModal';
import { CountryDetailsModal } from './map/CountryDetailsModal';

type Props = {
  width?: number;
  height?: number;
  stroke?: string;
  bg?: string;
  lineWidth?: number;
  showGraticule?: boolean;
  hoverStroke?: string;
  showPoints?: boolean;
  pointCount?: number;
  siteData?: SiteData[];
  onMapClick?: () => void;
};

type CountryFeature = GeoJSON.Feature<GeoJSON.MultiPolygon | GeoJSON.Polygon> & {
  id?: string | number;
  properties: {
    name?: string;
    id?: string | number;
    [k: string]: any;
  };
};

const WorldWireframeMap: React.FC<Props> = ({
  width,
  height = 480,
  stroke = "#16346A",
  bg = "transparent",
  lineWidth = 0.8,
  showGraticule = true,
  hoverStroke = "#2C6CF6",
  showPoints = true,
  pointCount = 220,
  siteData = [],
  onMapClick,
}) => {
  const [countries, setCountries] = useState<CountryFeature[]>([]);
  const [countryMesh, setCountryMesh] = useState<GeoJSON.Feature<GeoJSON.MultiLineString> | null>(null);
  const [land, setLand] = useState<GeoJSON.Feature<GeoJSON.MultiPolygon | GeoJSON.Polygon> | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(width ?? 0);
  const [selectedBubble, setSelectedBubble] = useState<BubbleData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [isCountryModalOpen, setIsCountryModalOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Use optimized interaction hooks
  const {
    hovered,
    hoveredBubble,
    mousePos,
    scale,
    handleWheel,
    handleMouseMove,
    handleBubbleEnter,
    handleBubbleLeave,
    handleCountryEnter,
    handleCountryLeave,
    resetScale,
  } = useMapInteractions();

  // Responsive width
  useEffect(() => {
    if (width) return;
    const el = wrapRef.current;
    if (!el) return;
    const resize = () => setContainerWidth(el.clientWidth);
    resize();
    const obs = new ResizeObserver(resize);
    obs.observe(el);
    return () => obs.disconnect();
  }, [width]);

  // Fetch world data - optimized with error handling
  useEffect(() => {
    let isMounted = true;

    async function load() {
      try {
        const countriesTopoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json";
        
        const countriesTopo = await fetch(countriesTopoUrl).then((r) => {
          if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
          return r.json();
        });

        const countriesGeo: any = feature(countriesTopo, countriesTopo.objects.countries);
        const meshLines: any = mesh(countriesTopo, countriesTopo.objects.countries, (a, b) => a !== b);
        const landGeo: any = feature(countriesTopo, countriesTopo.objects.land || countriesTopo.objects.countries);

        // Add country names with better error handling
        const withNames = (countriesGeo.features as any[]).map((c: any, index: number) => {
          const name = c.properties?.NAME || c.properties?.name || c.properties?.NAME_EN || `Country ${index + 1}`;
          c.properties = { ...(c.properties || {}), name, id: c.id || index };
          return c as CountryFeature;
        });

        if (!isMounted) return;
        setCountries(withNames);
        setCountryMesh(meshLines);
        setLand(landGeo);
      } catch (error) {
        console.error('Error loading world data:', error);
        if (isMounted) {
          setCountries([]);
          setCountryMesh(null);
          setLand(null);
        }
      }
    }

    load();
    return () => { isMounted = false; };
  }, []);

  const w = width ?? (containerWidth || 900);
  const h = height;

  // Optimized projection with scale
  const projection = useMemo(
    () => {
      const proj = d3.geoNaturalEarth1().fitExtent([[20, 20], [w - 20, h - 20]], { type: "Sphere" } as any);
      proj.scale(proj.scale() * scale);
      return proj;
    },
    [w, h, scale]
  );

  const path = useMemo(() => d3.geoPath(projection), [projection]);

  // Use optimized map data hook
  const { bubbleData } = useMapData(siteData, projection, countries);

  // Build graticule
  const graticule = useMemo(() => d3.geoGraticule10(), []);

  // Optimized wireframe points and links
  const points = useMemo(() => {
    if (!land || !showPoints) return [];
    const pts: [number, number][] = [];
    const bbox = path.bounds(land);
    const maxTries = pointCount * 20; // Reduced for performance
    let tries = 0;
    
    while (pts.length < pointCount && tries < maxTries) {
      tries++;
      const x = Math.random() * (bbox[1][0] - bbox[0][0]) + bbox[0][0];
      const y = Math.random() * (bbox[1][1] - bbox[0][1]) + bbox[0][1];
      const p = projection.invert!([x, y]);
      if (p && d3.geoContains(land as any, p)) pts.push([x, y]);
    }
    return pts;
  }, [land, pointCount, path, showPoints, projection]);

  // Optimized links calculation
  const links = useMemo(() => {
    if (!points.length || !showPoints) return [];
    const links: Array<[[number, number], [number, number]]> = [];
    const k = Math.max(2, Math.floor(Math.sqrt(points.length) / 3)); // Reduced connections for performance
    
    points.forEach((p, i) => {
      if (i % 3 !== 0) return; // Skip some points for performance
      
      const candidates = points
        .map((q, j) => ({ j, d: (q[0] - p[0]) ** 2 + (q[1] - p[1]) ** 2 }))
        .sort((a, b) => a.d - b.d)
        .slice(1, k + 1);
        
      candidates.forEach(({ j }) => {
        if (j > i) links.push([p, points[j]]);
      });
    });
    return links;
  }, [points, showPoints]);

  // Zoom control handlers
  const handleZoomIn = useCallback(() => {
    handleWheel({ deltaY: -100, preventDefault: () => {}, stopPropagation: () => {} } as any);
  }, [handleWheel]);

  const handleZoomOut = useCallback(() => {
    handleWheel({ deltaY: 100, preventDefault: () => {}, stopPropagation: () => {} } as any);
  }, [handleWheel]);

  const handleReset = useCallback(() => {
    resetScale();
  }, [resetScale]);

  // Optimized mouse move handler
  const onMouseMove = useCallback((e: React.MouseEvent) => {
    handleMouseMove(e, wrapRef);
  }, [handleMouseMove]);

  // Handle bubble click
  const handleBubbleClick = useCallback((bubble: BubbleData) => {
    setSelectedBubble(bubble);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedBubble(null);
  }, []);

  // Handle country click
  const handleCountryClick = useCallback((countryName: string) => {
    setSelectedCountry(countryName);
    setIsCountryModalOpen(true);
  }, []);

  const handleCloseCountryModal = useCallback(() => {
    setIsCountryModalOpen(false);
    setSelectedCountry('');
  }, []);

  // Handle site click from country modal
  const handleSiteClickFromCountry = useCallback((site: SiteData) => {
    // Create a bubble data structure for the site to reuse existing modal
    const siteBubble: BubbleData = {
      location: site.siteName,
      x: 0,
      y: 0,
      radius: 10,
      sites: [site],
      totalSites: 1,
      activeSites: site.isOnline ? 1 : 0,
      sessionsLastMonth: site.numSessionsLastMonth || 0,
      sessions12Months: site.numSessionsOnServerLast12Month || 0,
      isHealthy: site.isOnline
    };
    setSelectedBubble(siteBubble);
    setIsModalOpen(true);
    // Close country modal first
    setIsCountryModalOpen(false);
  }, []);

  // Handle SVG click (for empty space clicks)
  const handleSvgClick = useCallback((e: React.MouseEvent) => {
    // Only trigger map click if clicking on the SVG background (not on countries or bubbles)
    if (e.target === e.currentTarget && onMapClick) {
      onMapClick();
    }
  }, [onMapClick]);

  // Filter sites by selected country
  const countrySites = siteData.filter(site => site.country === selectedCountry);

  return (
    <div
      ref={wrapRef}
      className="relative overflow-hidden rounded-xl"
      style={{
        width: width ? `${width}px` : "100%",
        height: h,
        background: bg,
      }}
    >
      {/* Map Controls */}
      <MapControls
        scale={scale}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onReset={handleReset}
      />

      {/* Tooltips */}
      <MapTooltip
        hoveredBubble={hoveredBubble}
        hovered={hovered}
        mousePos={mousePos}
        width={w}
        onCountryClick={handleCountryClick}
      />

      <svg 
        width={w} 
        height={h} 
        role="img" 
        aria-label="Interactive world map with site data"
        onWheel={handleWheel}
        onMouseMove={onMouseMove}
        onClick={handleSvgClick}
        style={{ cursor: scale > 1 ? 'grab' : 'default' }}
        className="select-none"
      >
        {/* Filters for visual effects */}
        <defs>
          <filter id="soft-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="4" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Graticule lines */}
        {showGraticule && (
          <path
            d={path(graticule)!}
            fill="none"
            stroke={stroke}
            strokeOpacity={0.25}
            strokeWidth={lineWidth * 0.8}
            style={{ filter: "url(#soft-glow)" }}
          />
        )}

        {/* Country fills */}
        <g>
          {countries.map((c) => (
            <path
              key={`fill-${String(c.id)}`}
              d={path(c)!}
              fill={stroke}
              fillOpacity={0.1}
              stroke={stroke}
              strokeWidth={lineWidth * 0.5}
              strokeOpacity={0.6}
            />
          ))}
        </g>

        {/* Country borders mesh */}
        {countryMesh && (
          <path
            d={path(countryMesh)!}
            fill="none"
            stroke={stroke}
            strokeWidth={lineWidth}
            strokeOpacity={0.9}
          />
        )}

        {/* Interactive country paths */}
        <g>
          {countries.map((c) => (
            <path
              key={`hover-${String(c.id)}`}
              d={path(c)!}
              fill={hovered?.id === c.id ? hoverStroke : "transparent"}
              fillOpacity={hovered?.id === c.id ? 0.2 : 0}
              stroke={hovered?.id === c.id ? hoverStroke : "transparent"}
              strokeWidth={hovered?.id === c.id ? Math.max(1.5, lineWidth + 0.6) : lineWidth}
              onMouseEnter={() => handleCountryEnter(c)}
              onMouseLeave={handleCountryLeave}
              onFocus={() => handleCountryEnter(c)}
              onBlur={handleCountryLeave}
              onClick={(e) => {
                e.stopPropagation();
                if (c.properties?.name) {
                  handleCountryClick(c.properties.name);
                }
              }}
              tabIndex={0}
              aria-label={c.properties?.name}
              style={{ cursor: "pointer" }}
            />
          ))}
        </g>

        {/* Site bubbles */}
        <MapBubbles
          bubbleData={bubbleData}
          onBubbleEnter={handleBubbleEnter}
          onBubbleLeave={handleBubbleLeave}
          onBubbleClick={handleBubbleClick}
        />

        {/* Wireframe points and links */}
        {showPoints && (
          <g opacity={0.6}>
            {links.map(([a, b], i) => (
              <line
                key={`L${i}`}
                x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]}
                stroke={stroke}
                strokeOpacity={0.25}
                strokeWidth={Math.max(0.5, lineWidth * 0.7)}
              />
            ))}
            {points.map((p, i) => (
              <circle
                key={`P${i}`}
                cx={p[0]} cy={p[1]} r={1.2}
                fill={stroke}
                fillOpacity={0.55}
              />
            ))}
          </g>
        )}
      </svg>

      {/* Site Details Modal */}
      {selectedBubble && (
        <SiteDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          location={selectedBubble.location}
          sites={selectedBubble.sites}
          isHealthy={selectedBubble.isHealthy}
        />
      )}

      {/* Country Details Modal */}
      <CountryDetailsModal
        isOpen={isCountryModalOpen}
        onClose={handleCloseCountryModal}
        countryName={selectedCountry}
        sites={countrySites}
        onSiteClick={handleSiteClickFromCountry}
      />
    </div>
  );
};

export default WorldWireframeMap;